import { AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { Tag, TagsResponse } from '@/types/tag';
import { isTagResponse } from '@/ui/components/TagsEditor/types';
import ajax from '@/utils/ajax';

export const tagsRepository = {
  getPathFromContentType(contentType: string) {
    switch (contentType) {
      case 'items':
        return 'work';
      default:
        return 'dugout';
    }
  },

  async updateTags({
    id,
    contentType,
    tags,
    host
  }: {
    id: string;
    contentType: string;
    tags: Tag[];
    host: string;
  }): Promise<TagsResponse> {
    const response = await ajax.post<{ tags_slugs: string[] }, AxiosResponse<TagsResponse>>({
      url: `${host}/api/${this.getPathFromContentType(contentType)}/${contentType}/${id}/tags/add/`,
      data: { tags_slugs: tags.map((tag) => tag.slug) }
    });

    if (!isTagResponse(response.data)) throw new Error('Invalid response');

    return response.data;
  },

  async loadTags({
    id,
    contentType,
    host
  }: {
    id: string;
    contentType: string;
    host: string;
  }): Promise<TagsResponse> {
    const response = await ajax.get({
      url: `${host}/api/${this.getPathFromContentType(contentType)}/${contentType}/${id}/tags/`
    });

    if (!isTagResponse(response.data)) throw new Error('Invalid response');

    return response.data;
  },

  async getAvailableTagsForContentType({
    contentType,
    host
  }: {
    contentType: string;
    host: string;
  }): Promise<Tag[]> {
    const url = contentType === 'items' ? '/api/kustomer/tags/' : null;

    if (!url) {
      throw new Error('Invalid contentType for tags');
    }
    const { data } = await ajax.get({
      url: `${host}${url}`
    });

    return data;
  }
};

const TagsRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async updateTags({
      id,
      contentType,
      tags
    }: {
      id: string;
      contentType: string;
      tags: Tag[];
    }): Promise<TagsResponse> {
      return tagsRepository.updateTags({ id, contentType, tags, host });
    },

    async loadTags({
      id,
      contentType
    }: {
      id: string;
      contentType: string;
    }): Promise<TagsResponse> {
      return tagsRepository.loadTags({ id, contentType, host });
    },

    async getAvailableTagsForContentType({ contentType }: { contentType: string }): Promise<Tag[]> {
      return tagsRepository.getAvailableTagsForContentType({ contentType, host });
    }
  };
};

export default TagsRepository;
